import { Flex } from 'grid-styled';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedHTMLMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { get } from 'lodash';

import Button, { ButtonVariant } from '@gravity/button';
import Icon from '@gravity/icon';
import StylingConstants from '@gravity/styling-constants';
import { evolutionTheme } from '@gravity/theme-mapper';
import { DEFAULT_THEME_CONTEXT, TicketShopTheme, withThemeConsumer } from '@gravity/theme-provider';
import {
  classNames,
  withThemedStyles,
  withThemeQueries,
  WithThemeQueriesType,
} from '@gravity/utils';

const Wrapper = withThemedStyles(
  styled.div.attrs({
    className: 'g-ViewSelector',
  }),
  {
    [TicketShopTheme.EVOLUTION]: css`
      margin: 0 12px;
    `,
  },
);

const HeaderWrapper = withThemedStyles(
  styled(Flex).attrs({
    className: 'g-ViewSelector-header',
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      width: 100%;
      align-items: center;
      justify-content: center;
      padding: 0 1.5rem;

      ${StylingConstants.mediaQueries.desktop`
        justify-content: flex-end;
      `};
    `,
    [TicketShopTheme.EVOLUTION]: css`
      width: 100%;
      align-items: center;
      justify-content: flex-end;
    `,
  },
);

const MessageWrapper = styled.div.attrs({
  className: 'g-ViewSelector-label',
})`
  display: none;
`;

const borderRadiusDefault = '0px';

const ListWrapper = withThemedStyles(
  styled.div.attrs({
    className: 'g-ViewSelector-list',
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      button:first-child {
        border-top-left-radius: ${props =>
          (props.theme &&
            props.theme.viewSelector &&
            props.theme.viewSelector.buttonBorderRadius) ||
          borderRadiusDefault};
        border-bottom-left-radius: ${props =>
          (props.theme &&
            props.theme.viewSelector &&
            props.theme.viewSelector.buttonBorderRadius) ||
          borderRadiusDefault};
        border: 1px solid
          ${props =>
            (props.theme &&
              props.theme.viewSelector &&
              props.theme.viewSelector.selectedButtonBgColor) ||
            '#1a84f1'};
        border-right: none;
      }
      button:last-child {
        border-top-right-radius: ${props =>
          (props.theme &&
            props.theme.viewSelector &&
            props.theme.viewSelector.buttonBorderRadius) ||
          borderRadiusDefault};
        border-bottom-right-radius: ${props =>
          (props.theme &&
            props.theme.viewSelector &&
            props.theme.viewSelector.buttonBorderRadius) ||
          borderRadiusDefault};
        border: 1px solid
          ${props =>
            (props.theme &&
              props.theme.viewSelector &&
              props.theme.viewSelector.selectedButtonBgColor) ||
            '#1a84f1'};
        border-left: none;
      }
      ${StylingConstants.mediaQueries.desktop`
      margin-left: 1em;
    `};
    `,
    [TicketShopTheme.EVOLUTION]: css`
      display: flex;
      background: ${({ theme }) => theme.contentBgColor || '#ffffff'};
      border-radius: 24px;
      padding: 4px 6px;
      gap: ${({ theme }) => theme.spacingX || '12px'};
    `,
  },
);

const OptionWrapper = withThemedStyles(
  styled(Button).attrs({
    className: 'g-ViewSelector-view-option',
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      color: ${props =>
        (props.theme && props.theme.viewSelector && props.theme.viewSelector.iconColor) ||
        '#3B3D40'};
      background-color: ${props =>
        (props.theme && props.theme.viewSelector && props.theme.viewSelector.buttonBgColor) ||
        'transparent'};
      &.selected {
        color: #ffffff;
        background-color: ${props =>
          (props.theme &&
            props.theme.viewSelector &&
            props.theme.viewSelector.selectedButtonBgColor) ||
          '#1a84f1'};
      }
    `,
    [TicketShopTheme.EVOLUTION]: css`
      border-radius: 100px;
      padding: ${({ theme }) => theme.spacingY || '16px'};
      width: 48px;
      background: ${({ theme }) => theme.contentBgColor || '#ffffff'};
      padding: 8px 12px;
      gap: 10px;
      align-self: stretch;
      &.selected {
        color: #ffffff;
        border: none;
        background-color: var(--primaryColor);
        svg {
          color: #ffffff;
        }
      }
      &:hover.g-Button-secondary {
        opacity: 0.6;
        border: none;
      }
      .g-Icon.g-Icon-riCalendarEventLine {
        width: 20px;
      }
    `,
  },
);

const BodyWrapper = withThemedStyles(styled.div.attrs({
  className: 'g-ViewSelector-view-content',
}),{
  [TicketShopTheme.EVOLUTION]: css`
    background: ${({ theme }) => theme.contentBgColor || '#ffffff'};;
    border-radius: var(--generalBorderRadius, 12px);
    padding: ${evolutionTheme.spacings.xl}px;
    @media (max-width: 768px) {
        padding: ${({ theme }) => theme.spacingX || '12px'};
    }
    .stx-ProductList .g-Tabs-list {
        padding: 0;
    }

    section.g-Tabs-panel {
      padding: 0;
      padding-top: ${({ theme }) => theme.spacingX || '12px'};
    }
  `
});

/**
 * Render a component toogle view
 *
 * @param {object} config, object with the configuration of the site
 */
class ViewSelector extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      active: props.defaultView,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.defaultView !== this.state.active) {
      this.setState(prevState => ({ ...prevState, active: nextProps.defaultView }));
    }
  }

  /**
   * Method to return the content must be displayed depending of the selected view
   */
  getContent = () => this.props.views.find(view => view.id === this.state.active);

  /**
   * When the view is changed, we reload the component with the new selection
   */
  updateView = view => () => {
    if (this.state.active !== view) {
      this.setState(
        prevState => ({ ...prevState, active: view }),
        this.props.onChange ? this.props.onChange(view) : null,
      );
    }
  };

  render() {
    const { views, themeQueries } = this.props;
    const activeView = this.state.active ? this.getContent() : null;
    const isActiveView = view => view.id === activeView.id;
    const optionButtonVariant = isActive => {
      if (themeQueries.EVOLUTION) {
        return isActive ? ButtonVariant.primary : ButtonVariant.secondary;
      }
      return ButtonVariant.primary;
    };
    return (
      <Wrapper>
        {activeView && (
          <div>
            <HeaderWrapper>
              <MessageWrapper>
                <FormattedHTMLMessage id={`gravity.ViewSelector.select`} />
              </MessageWrapper>
              <ListWrapper>
                {views.map(view => (
                  <OptionWrapper
                    variant={optionButtonVariant(isActiveView(view))}
                    key={view.id}
                    className={classNames({
                      selected: isActiveView(view),
                    })}
                    onClick={this.updateView(view.id)}>
                    {view.icon && <Icon size={16} iconName={view.icon} />}
                    {view.label && (
                      <FormattedHTMLMessage id={`gravity.ProductList.tab.${view.label}`} />
                    )}
                  </OptionWrapper>
                ))}
              </ListWrapper>
            </HeaderWrapper>
            <BodyWrapper className="content">{activeView.content}</BodyWrapper>
          </div>
        )}
      </Wrapper>
    );
  }
}

ViewSelector.propTypes = {
  /** List of view will be created
   * id: string with the id of the view
   * icon: identificator of the icon to display in the button. If empty, no icon will be included
   * label: label will be shown in the button. If empty, no label will be included
   * content: content to display when the view is selected
   */
  views: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.string,
      label: PropTypes.string,
      content: PropTypes.node,
    }),
  ),
  /** Identificador (id) of the view selected by default */
  defaultView: PropTypes.string,
  /** Method to pass to the parent the information of the view selected */
  onChange: PropTypes.func,
  /** theme context */
  themeQueries: WithThemeQueriesType,
  currentTheme: PropTypes.string,
};

ViewSelector.defaultProps = {
  views: [{ id: 'list' }, { id: 'calendar' }],
  defaultView: 'list',
  onChange: null,
  themeContext: DEFAULT_THEME_CONTEXT,
  themeQueries: {},
  currentTheme: DEFAULT_THEME_CONTEXT.theme,
};

export default withThemeConsumer(withThemeQueries(ViewSelector));
